import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { DocumentIcon, PDFIcon } from "../../assets/assets";
import dropDownArrow from "../../assets/DropDownArrow.svg";
import unsupportedIcon from "../../assets/Unsupported.svg";
import "../../design-system.css";
import { dateAndTimeConverter } from "../../utils/constants";
import RoundedProfilePicture from "../RoundedProfilePicture/RoundedProfilePicture";
import "./FHIRRenderer.css";

export default function FHIRRenderer(props) {
  return (
    <div data-cy="FHIR-Component" className="inherit-parent-width">
      <div className="border-bottom-2px padding-top-medium padding-bottom-medium padding-left-larger padding-right-larger">
        <div className=" display-flex flex-align-items-center flex-justify-content-space-between">
          <div className="flex-center-children-vertically font-family-gilroy-regular  padding-right-larger">
            <RoundedProfilePicture
              data-cy="Organization-profile-picture"
              size="small"
              userType="corporate"
              removeResponsive={true}
              src={getClinicSrc(
                props.userType,
                props.connection,
                props.document
              )}
            />
            <div className="padding-left-large">
              <div className="font-size-large padding-right-default text-capitalize">
                {props.document?.clinicName}
              </div>
              <div className=" font-size-small padding-top-small">
                {props.document?.locality ? props.document?.locality : ""}
              </div>
            </div>
          </div>

          <div className=" font-size-small white-space-no-wrap">
            <div data-cy={"UPLOADED-DATE"}>
              {dateAndTimeConverter(
                +new Date(props.data.timestamp),
                "secondDate"
              )}
            </div>
            <div
              className=" padding-top-small white-space-no-wrap"
              data-cy={"UPLOADED-TIME"}
            >
              {dateAndTimeConverter(+new Date(props.data.timestamp), "Time")}
            </div>
          </div>
        </div>
      </div>

      {(props.data?.entry?.some(
        (d) => d.resource?.resourceType === "DocumentReference"
      ) ||
        props.data?.entry?.some(
          (d) =>
            d.resource?.resourceType === "DiagnosticReport" &&
            (d.resource?.presentedForm?.[0]?.data ||
              d.resource?.presentedForm?.[0]?.url)
        ) ||
        // some bundles (e.g. prescriptions) carry the document as a top-level
        // Binary instead of a DocumentReference/DiagnosticReport. DocumentList
        // already handles these, so include them here or the preview never shows.
        props.data?.entry?.some(
          (d) =>
            d.resource?.resourceType === "Binary" &&
            (d.resource?.data || d.resource?.url)
        )) && (
        <div className="border-bottom-2px padding-bottom-large">
          <DocumentList
            data={props.data}
            documentOnClick={props.documentOnClick}
          />
        </div>
      )}

      {props.data?.entry?.[0]?.resource && (
        <div>
          <Composition
            data={props.data?.entry?.[0]?.resource}
            value={props.data}
          />
        </div>
      )}
      <div className="padding-default overflow-auto">
        {props.data?.entry?.[0]?.resource?.section?.map((data, index) => (
          <div
            className="margin-bottom-default padding-left-larger padding-right-larger"
            data-cy={`accordion-${data.title}-card`.toUpperCase()}
            key={index}
          >
            <Accordion
              value={props.data}
              data={data}
              documentOnClick={props.documentOnClick}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const getClinicSrc = (type, connection, document) => {
  return type === "clinic"
    ? connection?.brands?.[connection.data?.[document?.clinicId]?.brandId]
        ?.downloadURL
    : null;
};

const searchResource = (data, resourceType) => {
  return data?.entry?.find(
    (entry) => entry?.resource?.resourceType === resourceType
  )?.resource;
};

// CodeableConcept -> human-readable label. NDHM marks `.text` as the
// Must-Support element, so prefer it and fall back to the first coding's
// display. Use everywhere a CodeableConcept is shown so both text-only and
// coding-only values render (neither goes blank).
const codeableConceptText = (concept) =>
  concept?.text ?? concept?.coding?.[0]?.display;

const BasicInfo = (props) => {
  const [data, setData] = useState({
    clinic: "",
    doctor: "",
    patient: ""
  });

  useEffect(() => {
    setData({
      clinic: searchResource(props.data, "Organization"),
      doctor: searchResource(props.data, "Practitioner"),
      patient: searchResource(props.data, "Patient")
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="display-flex padding-top-default padding-bottom-default padding-left-large padding-right-large flex-justify-content-space-between adaptive-flex-direction">
      {data?.clinic?.name && (
        <div
          data-cy="Organization-card"
          className="padding-default display-flex"
        >
          <div className="font-family-gilroy-medium">Source &nbsp; </div>
          :&nbsp;
          {data?.clinic?.name}
        </div>
      )}
      {data?.patient?.name && (
        <div
          data-cy="Patient-card"
          className="padding-default display-medium display-flex"
        >
          <div className="font-family-gilroy-medium">Patient &nbsp; </div>
          :&nbsp;
          {data?.patient?.name?.[0]?.text ?? "--"}
        </div>
      )}
      {data?.doctor?.name && (
        <div
          data-cy="Practitioner-card"
          className="padding-default display-medium display-flex"
        >
          <div className=" font-family-gilroy-medium">Handled By &nbsp; </div>
          :&nbsp;
          {data?.doctor?.name?.[0]?.prefix?.[0] || ""}.
          {data?.doctor?.name?.[0]?.text || ""}{" "}
          {data?.doctor?.name?.[0]?.suffix?.[0] || ""}
        </div>
      )}
    </div>
  );
};

const Accordion = (props) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <div
        className={`display-flex flex-justify-content-space-between padding-large ${
          show ? "" : "border-bottom-1px"
        }`}
        onClick={() => {
          setShow((prev) => !prev);
        }}
      >
        <div
          className={`${
            show ? "font-family-gilroy-medium " : ""
          } font-size-default`}
        >
          {props.data.title}
        </div>
        <DropDownButton
          dropDownButton-data-cy="title-dropDown"
          showData={show}
        />
      </div>
      {show && (
        <div className="border-bottom-1px">
          {props.data.entry?.map((data, index) => (
            <div key={index}>
              {getComponents({
                entryData: props.value,
                type:
                  typeof data?.reference === "string"
                    ? getResourceData(props.value, data?.reference)
                        ?.resourceType
                    : null,
                index,
                data,
                documentOnClick: props.documentOnClick
              })}
            </div>
          ))}
          <BasicInfo data={props.value} />
        </div>
      )}
    </>
  );
};

const DocumentList = (props) => {
  const documents =
    props.data?.entry?.filter(
      (d) => d.resource?.resourceType === "DocumentReference"
    ) || [];

  const reports =
    props.data?.entry?.filter(
      (d) =>
        d.resource?.resourceType === "DiagnosticReport" &&
        // only list reports that carry an actual document; a DiagnosticReport
        // whose PDF lives in a sibling DocumentReference has no presentedForm
        // and would otherwise render as an empty, crash-on-click card
        (d.resource?.presentedForm?.[0]?.data ||
          d.resource?.presentedForm?.[0]?.url)
    ) || [];

  // some bundles (e.g. prescriptions) attach the PDF as a top-level Binary
  // instead of wrapping it in a DocumentReference. Binary carries the document
  // (base64 data / url) directly on the resource, so surface those too.
  const binaries =
    props.data?.entry?.filter(
      (d) =>
        d.resource?.resourceType === "Binary" &&
        (d.resource?.data || d.resource?.url)
    ) || [];

  const [showAll, setShowAll] = useState(false);

  const allDocs = [...documents, ...reports, ...binaries];

  const visibleDocs = showAll ? allDocs : allDocs.slice(0, 1);

  return (
    <>
      {visibleDocs.map((d) => (
        <DocumentView
          key={d?.resource?.id}
          data={d?.resource}
          documentOnClick={props.documentOnClick}
        />
      ))}
      {allDocs.length > 1 && (
        <div className="inherit-parent-width display-flex flex-justify-content-center padding-left-larger padding-right-larger padding-top-default padding-bottom-default">
          <button onClick={() => setShowAll((prev) => !prev)} className="">
            {showAll ? "View less" : "View more"}
          </button>
        </div>
      )}
    </>
  );
};

const Practitioner = (props) => {
  const phoneNumber = props.data?.telecom; // 0..*
  const address = props.data?.address?.[0]; // address is 0..*
  const identifier = props.data?.identifier?.[0]; // identifier 1..* (mandatory)
  const qualifications = props.data?.qualification; // 0..* — ARRAY, not an object

  // Address.line is 0..* (array of strings) — compose the full address
  const addressText = address
    ? [
        Array.isArray(address.line) ? address.line.join(", ") : address.line,
        address.city,
        address.state,
        address.postalCode
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="Practitioner-card"
    >
      <TitleFields title="Practitioner" />
      <div className="padding-medium">
        <DataFields title="Name" data={props.data?.name?.[0]?.text} />

        {/* FIX: qualification is 0..* — index into the array (and map all of them) */}
        {qualifications?.length > 0 && (
          <DataFields
            title="Qualification"
            data={qualifications.map((q, index) => (
              <div
                className="font-size-large"
                key={`Practitioner-qual-${index}`}
              >
                {codeableConceptText(q?.code) ?? "--"}
              </div>
            ))}
          />
        )}

        {props.data?.gender && (
          <DataFields title="Gender" data={props.data?.gender} />
        )}
        {props.data?.birthDate && (
          <DataFields title="DOB" data={props.data?.birthDate} />
        )}

        {/* identifier mandatory; value 1..1. Label via type, with fallback */}
        {identifier?.value && (
          <DataFields
            title={codeableConceptText(identifier?.type) ?? "Identifier"}
            data={identifier?.value}
          />
        )}
        {identifier?.assigner?.display && (
          <DataFields title="Assigner" data={identifier?.assigner?.display} />
        )}

        {/* telecom 0..*; system is 0..1 — guard it so you don't print "undefined : ..." */}
        {phoneNumber?.length > 0 && (
          <DataFields
            title="Telecom"
            data={phoneNumber.map((data, index) => (
              <div
                className="font-size-large"
                key={`Practitioner-tel-${index}`}
              >
                {data?.system ? `${data.system} : ` : ""}
                {data?.value ?? "--"}
              </div>
            ))}
          />
        )}

        {/* address 0..*; line is 0..* array */}
        {addressText && <DataFields title="Address" data={addressText} />}
      </div>
    </div>
  );
};

const Composition = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="Composition-card"
    >
      <DataFields title="Title" data={props.data?.title} />
      <DataFields title="Status" data={props.data?.status} />
      <DataFields title="Type" data={codeableConceptText(props.data?.type)} />
      <DataFields
        title="Date"
        data={`${dateAndTimeConverter(props.data?.date, "secondDate")} ${" | "}
        ${dateAndTimeConverter(props.data?.date, "Time")}`}
      />
      <DataFields
        title="Subject"
        data={
          getResourceData(props.value, props.data?.subject?.reference)
            ?.name?.[0]?.text
        }
      />
      {/* author is 1..* — render every author, not just the first */}
      <DataFields
        title="Author"
        data={props.data?.author?.map((author, index) => (
          <div className="font-size-large" key={`composition-author-${index}`}>
            {author?.display ?? author?.reference}
          </div>
        ))}
      />
      {props.data?.encounter && (
        <DataFields
          title="Encounter"
          data={
            <Encounter
              value={props.value} // ADDED: pass value so Encounter can resolve subject
              data={getResourceData(
                props.value,
                props.data?.encounter?.reference
              )}
            />
          }
        />
      )}
    </div>
  );
};

const Patient = (props) => {
  const name = props?.data?.name?.[0]; // name is 0..* (Must Support, NOT mandatory); name.text is 1..1
  const identifier = props.data?.identifier?.[0]; // identifier is 1..* (mandatory); value is 1..1

  return (
    <div className="padding-larger" data-cy="Patient-card">
      <div className="font-size-small text-uppercase">Patient</div>
      <div className="flex-center-children-vertically flex-justify-content-space-between flex-direction-row">
        <div className="padding-top-small">
          <div className="font-size-large">{name?.text ?? "--"}</div>

          {/* identifier is mandatory — show the health ID, not the logical resource id */}
          {identifier?.value && (
            <div className="font-size-small padding-top-default">
              {codeableConceptText(identifier?.type) ?? "ID"}:{" "}
              {identifier?.value}
            </div>
          )}

          {props.data?.gender && (
            <div className="font-size-small padding-top-small text-transform-capitalize">
              {props.data?.gender}
            </div>
          )}

          {props.data?.birthDate && (
            <div className="font-size-small padding-top-small">
              {props.data?.birthDate}
            </div>
          )}

          {props.data?.telecom?.length > 0 && (
            <div className="font-size-small padding-top-small">
              {props.data.telecom.map((t, i) => (
                <div key={`patient-tel-${i}`}>
                  {t?.system ? `${t.system}: ` : ""}
                  {t?.value ?? "--"}
                </div>
              ))}
            </div>
          )}

          {props.data?.address?.[0] && (
            <div className="font-size-small padding-top-small">
              {[
                Array.isArray(props.data.address[0].line)
                  ? props.data.address[0].line.join(", ")
                  : props.data.address[0].line,
                props.data.address[0].city,
                props.data.address[0].state,
                props.data.address[0].postalCode
              ]
                .filter(Boolean)
                .join(", ")}
            </div>
          )}
        </div>
        <div>
          <RoundedProfilePicture
            data-cy="Patient-profile-picture"
            className="margin-top-default"
            size={"medium"}
          />
        </div>
      </div>
    </div>
  );
};

const Encounter = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`Encounter-card`}
    >
      <div className="padding-medium">
        <DataFields
          title="Identifier"
          data={props.data?.identifier?.[0]?.value}
        />
        <DataFields title="Status" data={props.data?.status} />
        {/* ADDED: class is mandatory in Encounter */}
        <DataFields
          title="Class"
          data={props.data?.["class"]?.display || props.data?.["class"]?.code}
        />
        {/* ADDED: subject (mandatory) */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        {props.data?.type?.[0] && (
          <DataFields
            title="Type"
            data={codeableConceptText(props.data?.type?.[0])}
          />
        )}
        {/* participant is 0..* — render every participant */}
        {props.data?.participant?.length > 0 && (
          <DataFields
            title="Participant"
            data={props.data?.participant?.map((participant, index) => (
              <div className="font-size-large" key={`encounter-part-${index}`}>
                {codeableConceptText(participant?.type?.[0]) ??
                  getResourceData(
                    props.value,
                    participant?.individual?.reference
                  )?.name?.[0]?.text ??
                  participant?.individual?.display}
              </div>
            ))}
          />
        )}
        {props.data?.priority && (
          <DataFields
            title="Priority"
            data={codeableConceptText(props.data?.priority)}
          />
        )}
        {props.data?.period && (
          <DataFields
            title="Period"
            data={`${dateAndTimeConverter(
              props.data?.period?.start,
              "secondDate"
            )}  | 
          ${dateAndTimeConverter(props.data?.period?.start, "Time")}`}
          />
        )}

        {props.data?.reasonCode?.length > 0 && (
          <DataFields
            title="Reason Code"
            data={props.data?.reasonCode?.map((reason, index) => (
              <div
                className="font-size-large"
                key={`encounter-reason-${index}`}
              >
                {codeableConceptText(reason)}
              </div>
            ))}
          />
        )}

        {/* FIX: diagnosis is 0..* — index into the array (was diagnosis.use) */}
        {props.data?.diagnosis?.length > 0 && (
          <DataFields
            title="Diagnosis"
            data={props.data?.diagnosis?.map((diagnosis, index) => (
              <div className="font-size-large" key={`encounter-diag-${index}`}>
                {codeableConceptText(diagnosis?.use) ??
                  getResourceData(props.value, diagnosis?.condition?.reference)
                    ?.code?.text ??
                  diagnosis?.condition?.display}
              </div>
            ))}
          />
        )}
        {/* FIX: hospitalization is a BackboneElement — render its fields,
            not the raw object (which crashes React) */}
        {props.data?.hospitalization && (
          <DataFields
            title="Hospitalization"
            data={
              codeableConceptText(props.data?.hospitalization?.admitSource) ??
              codeableConceptText(
                props.data?.hospitalization?.dischargeDisposition
              )
            }
          />
        )}
        {/* FIX: serviceType is top-level on Encounter, not under class */}
        {props.data?.serviceType && (
          <DataFields
            title="Service Type"
            data={codeableConceptText(props.data?.serviceType)}
          />
        )}
      </div>
    </div>
  );
};

const DateAndTime = (props) => {
  const dataAndTime = +new Date(props.data?.date);
  return (
    <div
      className="padding-larger flex-direction-row"
      data-cy="DateAndTime-card"
    >
      <div className=" width-50-percentage text-align-center">
        <div className="font-size-small text-uppercase">Date</div>
        <div className=" padding-top-default">
          {dateAndTimeConverter(dataAndTime, "secondDate")}
        </div>
      </div>
      <div className=" width-50-percentage text-align-center">
        <div className="font-size-small">Time</div>
        <div className=" padding-top-default">
          {dateAndTimeConverter(dataAndTime, "Time")}
        </div>
      </div>
    </div>
  );
};

const Medication = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`Medication-card`}
    >
      <TitleFields title="Medication" />

      <div className="padding-medium">
        <DataFields
          title="Identifier"
          data={codeableConceptText(props.data?.identifier?.[0]?.type)}
        />
        {/* code is mandatory (NDHM) */}
        <DataFields
          title="Medicine"
          data={codeableConceptText(props.data?.code)}
        />
        <DataFields
          title="Manufacturer"
          data={props.data?.manufacturer?.display}
        />
        <DataFields title="Form" data={codeableConceptText(props.data?.form)} />
        <DataFields title="Lot Number" data={props.data?.batch?.lotNumber} />
        <DataFields
          title="Expiration Date"
          data={props.data?.batch?.expirationDate}
        />
      </div>
    </div>
  );
};

const MedicationRequest = (props) => {
  const medicationReference = getResourceData(
    props.value,
    props.data?.medicationReference?.reference
  )?.code;

  // FIX: medication[x] is mandatory; render the actual medicine (was never shown)
  const medicineDisplay =
    codeableConceptText(props.data?.medicationCodeableConcept) ??
    codeableConceptText(medicationReference);

  const doctorInfo = getResourceData(
    props.value,
    props.data?.requester?.reference
  );
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`MedicationRequest-card-${medicineDisplay ?? "medicine"}`}
    >
      <TitleFields title="Medication Request" />

      <div className="padding-medium">
        {/* ADDED: medication[x] (mandatory) */}
        <DataFields title="Medication" data={medicineDisplay} />
        <DataFields title="Status" data={props.data?.status} />
        <DataFields title="Intent" data={props.data?.intent} />
        {/* reasonCode is 0..* — render every reason */}
        {props.data?.reasonCode?.length > 0 && (
          <DataFields
            title="Reason"
            data={props.data?.reasonCode?.map((reason, index) => (
              <div className="font-size-large" key={`medreq-reason-${index}`}>
                {codeableConceptText(reason)}
              </div>
            ))}
          />
        )}
        <DataFields title="Requester" data={props.data?.requester?.display} />
        {/* ADDED: subject (mandatory) */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        <DataFields
          title="Prescribed by"
          data={doctorInfo?.name?.[0]?.text ?? "--"}
        />

        {props.data?.authoredOn && (
          <DataFields
            title="Authored On"
            data={`${dateAndTimeConverter(
              +new Date(props.data.authoredOn),
              "secondDate"
            )}
          | 
         ${dateAndTimeConverter(+new Date(props.data.authoredOn), "Time")}`}
          />
        )}
        {props.data?.dosageInstruction && (
          <DataFields
            title="Dosage Instruction"
            data={props.data?.dosageInstruction?.map((data, index) => (
              <div
                className="padding-left-larger padding-right-larger"
                key={`medicationRequest-${index}`}
              >
                <div className="font-size-large text-capitalize padding-top-default">
                  {data.text}
                </div>
                <DataFields
                  title="Additional Instruction"
                  data={codeableConceptText(data?.additionalInstruction?.[0])}
                />
                {/* ADDED: dosage frequency (timing) */}
                <DataFields
                  title="Frequency"
                  data={
                    codeableConceptText(data?.timing?.code) ??
                    (data?.timing?.repeat?.frequency
                      ? `${data?.timing?.repeat?.frequency} time(s) / ${
                          data?.timing?.repeat?.period ?? ""
                        }${data?.timing?.repeat?.periodUnit ?? ""}`
                      : undefined)
                  }
                />
                {/* ADDED: dose quantity (doseAndRate) */}
                <DataFields
                  title="Dose"
                  data={
                    data?.doseAndRate?.[0]?.doseQuantity?.value
                      ? `${data?.doseAndRate?.[0]?.doseQuantity?.value} ${
                          data?.doseAndRate?.[0]?.doseQuantity?.unit ?? ""
                        }`
                      : undefined
                  }
                />
                <DataFields
                  title="Method"
                  data={codeableConceptText(data?.method)}
                />
                <DataFields
                  title="Route"
                  data={codeableConceptText(data?.route)}
                />
              </div>
            ))}
          />
        )}

        {props.data?.reasonReference?.map((reference, index) => (
          <DataFields
            key={`medreq-reasonref-${index}`}
            title="Reason"
            data={
              <Condition
                value={props.value}
                data={getResourceData(props.value, reference?.reference)}
              />
            }
          />
        ))}
      </div>
    </div>
  );
};

const DocumentView = (props) => {
  // Binary carries data/contentType directly on the resource; DocumentReference
  // nests it under content[0].attachment and DiagnosticReport under
  // presentedForm[0]. Treat the Binary itself as the resource in that case.
  const resource =
    props.data?.resourceType === "Binary"
      ? props.data
      : props.data?.content?.[0] || props.data?.presentedForm?.[0];
  const extension =
    resource?.attachment?.contentType?.split("/")?.[1] ||
    resource?.contentType?.split("/")?.[1];

  // a resource is only viewable when it actually carries a document — inline
  // base64 (data) or a fetchable URL. Without this, a documentless resource
  // renders a "View file" action that resolves to null and crashes the viewer.
  const hasDocument = Boolean(
    resource?.attachment?.data ||
      resource?.data ||
      resource?.url ||
      resource?.attachment?.url
  );

  async function getDocumentBuffer(resource) {
    if (resource?.attachment?.data) {
      const buffer = base64ToArrayBuffer(resource.attachment.data);
      return buffer ? new Uint8Array(buffer) : null;
    }

    if (resource?.data) {
      const buffer = base64ToArrayBuffer(resource.data);
      return buffer ? new Uint8Array(buffer) : null;
    }

    try {
      if (resource?.url) {
        const res = await fetch(resource.url);
        const buffer = await res.arrayBuffer();
        return new Uint8Array(buffer);
      }

      if (resource?.attachment?.url) {
        const res = await fetch(resource.attachment.url);
        const buffer = await res.arrayBuffer();
        return new Uint8Array(buffer);
      }
    } catch (error) {
      console.error("Failed to fetch document:", error);
      return null;
    }

    return null;
  }

  const handleView = async () => {
    const documentBuffer = await getDocumentBuffer(resource);
    // guard: never hand a null/empty buffer to the viewer — it crashes on it
    if (!documentBuffer) {
      console.error("No document available to view for this resource.");
      return;
    }
    props.documentOnClick?.(documentBuffer);
  };

  return (
    <div
      className="padding-left-larger padding-top-larger padding-right-larger"
      data-cy="DocumentReference-card"
    >
      <div className="display-flex flex-justify-content-space-between ">
        <div className=" flex-justify-content-space-between">
          <div style={{ height: "28px", width: "28px" }}>
            <DocumentIcon height="28" width="28" removeTickIcon={true} />
          </div>

          <div className="font-size-medium padding-left-large font-family-gilroy-regular display-flex flex-align-items-center padding-right-large">
            {resource?.attachment?.title ||
              resource?.title ||
              props.data?.id ||
              "--"}
            .{extension ?? "--"}
          </div>
        </div>
        {hasDocument && (
          <div
            onClick={handleView}
            className="font-size-medium display-flex flex-align-items-center font-family-gilroy-medium cursor-pointer font-color-primary white-space-no-wrap"
            data-cy={"VIEW-BUTTON"}
          >
            View file
          </div>
        )}
      </div>
    </div>
  );
};

// One content entry (DocumentReference.content is 1..*). Each carries its own
// attachment, so the buffer/view logic is scoped per content item.
const DocumentReferenceContent = (props) => {
  const resource = props.content;
  const extension = resource?.attachment?.contentType?.split("/")?.[1];

  async function getDocumentBuffer(resource) {
    if (resource?.attachment?.data) {
      const buffer = base64ToArrayBuffer(resource.attachment.data);
      return buffer ? new Uint8Array(buffer) : null;
    }

    try {
      if (resource?.attachment?.url) {
        const res = await fetch(resource.attachment.url);
        const buffer = await res.arrayBuffer();
        return new Uint8Array(buffer);
      }
    } catch (error) {
      console.error("Failed to fetch document:", error);
      return null;
    }

    return null;
  }

  const handleView = async () => {
    const documentBuffer = await getDocumentBuffer(resource);
    // guard: never hand a null/empty buffer to the viewer — it crashes on it
    if (!documentBuffer) {
      console.error("No document available to view for this resource.");
      return;
    }
    props.documentOnClick?.(documentBuffer);
  };

  return (
    <DataFields
      title="Data"
      data={
        resource?.attachment?.url ||
        allowSupportedFileFormat(resource?.attachment?.contentType) ? (
          <div
            className=" flex-justify-content-space-between flex-direction-column"
            onClick={handleView}
          >
            <div className="padding-top-small">
              <div className="font-size-large">
                {resource?.attachment?.title ?? "--"}.{extension ?? "--"}
              </div>
            </div>
            <div
              data-cy="pdfRenderer"
              className="padding-top-large padding-bottom-default width-40-percentage"
            >
              <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
                <PDFIcon height="48" width="48" />
                <div className="adaptive-padding-top">View file</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="padding-top-large padding-bottom-default width-40-percentage">
            <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
              <img
                data-cy="unSupported-icon"
                draggable="false"
                className="height-width-48px"
                src={unsupportedIcon}
                alt="unsupported icon"
              />
              <div className="adaptive-padding-top">UnSupported</div>
            </div>
          </div>
        )
      }
    />
  );
};

const DocumentReference = (props) => {
  return (
    <div
      className="padding-left-larger padding-top-larger padding-right-larger"
      data-cy="DocumentReference-card"
    >
      <TitleFields title="Document Reference" />
      <div className="padding-medium">
        <DataFields title="Status" data={props.data?.status} />
        {/* type is 0..1 (Must Support, not mandatory) */}
        <DataFields title="Type" data={codeableConceptText(props.data?.type)} />

        {/* content is 1..* — render every attachment */}
        {props.data?.content?.map((content, index) => (
          <DocumentReferenceContent
            key={`docref-content-${index}`}
            content={content}
            documentOnClick={props.documentOnClick}
          />
        ))}
      </div>
    </div>
  );
};

const Organization = (props) => {
  // name is 1..1 plain string — show it as-is, don't split on commas
  const name = props.data?.name;

  // locality/area belongs to address, not name
  const address = props.data?.address?.[0]; // address is 0..*
  const area =
    address?.city ?? address?.district ?? address?.state ?? undefined;

  const phoneNumber = props.data?.telecom; // 0..*
  const identifier = props.data?.identifier?.[0]; // 1..* (mandatory)

  // Address.line is 0..* (array of strings) — join, then append the rest
  const addressText = address
    ? [
        Array.isArray(address.line) ? address.line.join(", ") : address.line,
        address.city,
        address.state,
        address.postalCode
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  return (
    <div className="padding-larger" data-cy="Organization-card">
      <div className="padding-left-larger padding-right-larger">
        Organization
      </div>
      <div className="padding-medium">
        <div className="flex-center-children-vertically flex-justify-content-space-between flex-direction-row">
          <div className="padding-top-small">
            <div className="font-size-large">{name ?? "--"}</div>
            {area && (
              <div className="font-size-small padding-top-small">{area}</div>
            )}
          </div>
          <div className=" flex-center-children-horizontally flex-center-children-vertically">
            <RoundedProfilePicture
              data-cy="Organization-profile-picture"
              className="margin-top-default"
              size={"medium"}
            />
          </div>
        </div>

        {/* identifier is mandatory (1..*); value is 1..1. Label with type if present */}
        {identifier?.value && (
          <DataFields
            title={codeableConceptText(identifier?.type) ?? "Identifier"}
            data={identifier?.value}
          />
        )}

        {/* type is 0..* (Must Support) — render every type */}
        {props.data?.type?.length > 0 && (
          <DataFields
            title="Type"
            data={props.data?.type?.map((type, index) => (
              <div className="font-size-large" key={`org-type-${index}`}>
                {codeableConceptText(type)}
              </div>
            ))}
          />
        )}

        {/* telecom 0..*; value is 1..1 but system is 0..1 — guard system */}
        {phoneNumber?.length > 0 && (
          <>
            <div className="font-size-small text-uppercase padding-top-large">
              Contact
            </div>
            {phoneNumber.map((data, index) => (
              <div className="font-size-large" key={`organization-${index}`}>
                {data?.system ? `${data.system} : ` : ""}
                {data?.value ?? "--"}
              </div>
            ))}
          </>
        )}

        {/* address 0..*; line is 0..* array */}
        {addressText && <div className="padding-top-large">{addressText}</div>}
      </div>
    </div>
  );
};

const Immunization = (props) => {
  const vaccine = props.data?.vaccineCode;
  const reasonReference = props.data?.reasonReference;
  const reaction = props.data?.reaction;
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`Immunization-card-${codeableConceptText(vaccine) ?? "vaccine"}`}
    >
      <TitleFields title="Immunization" />

      <div className="padding-medium">
        <DataFields title="Title" data={codeableConceptText(vaccine)} />

        <DataFields title="Status" data={props.data?.status} />

        {/* ADDED: patient (mandatory) */}
        <DataFields
          title="Patient"
          data={
            getResourceData(props.value, props.data?.patient?.reference)
              ?.name?.[0]?.text ?? props.data?.patient?.reference
          }
        />

        <DataFields
          title="Brand"
          data={props.data?.extension?.[0]?.valueString}
        />

        <DataFields
          title="Manufacturer"
          data={
            getResourceData(props.value, props.data?.manufacturer?.reference)
              ?.name ?? props.data?.manufacturer?.display
          }
        />

        {/* occurrence[x] is mandatory */}
        <DataFields title="Occurrence" data={props.data?.occurrenceDateTime} />
        <DataFields
          title="Expiration Date"
          data={dateAndTimeConverter(props.data?.expirationDate, "cardDate")}
        />

        <DataFields title="LotNumber" data={props.data?.lotNumber} />

        <DataFields title="Site" data={codeableConceptText(props.data?.site)} />
        <DataFields
          title="Route"
          data={codeableConceptText(props.data?.route)}
        />
        <DataFields
          title="Performer"
          data={
            getResourceData(
              props.value,
              props.data?.performer?.[0]?.actor?.reference
            )?.name?.[0]?.text
          }
        />
        <DataFields
          title="Reason Code"
          data={codeableConceptText(props.data?.reasonCode?.[0])}
        />

        <DataFields
          title="Location"
          data={
            getResourceData(props.value, props.data?.location?.reference)
              ?.description
          }
        />

        <DataFields
          title="Dose Number"
          data={props.data?.protocolApplied?.[0]?.doseNumberPositiveInt}
        />
      </div>
      {reasonReference?.map((data, index) => {
        return (
          data?.reference?.split("/")[0] === "DiagnosticReport" && (
            <div className="padding-bottom-default" key={`imm-reason-${index}`}>
              <DiagnosticReport
                data={getResourceData(props.value, data?.reference)}
                value={props.value}
                documentOnClick={props.documentOnClick}
              />
            </div>
          )
        );
      })}
      {reaction?.map((data, index) => {
        return (
          data?.detail?.reference?.split("/")[0] === "Observation" && (
            <div
              className="padding-bottom-default"
              key={`imm-reaction-${index}`}
            >
              <Observation
                value={props.value}
                data={getResourceData(props.value, data?.detail?.reference)}
              />
            </div>
          )
        );
      })}
    </div>
  );
};

const Observation = (props) => {
  const renderObservationValue = (data, title) => {
    if (data?.valueQuantity) {
      return (
        <DataFields
          title={title}
          data={`${data?.valueQuantity?.value ?? "--"} ${
            data?.valueQuantity?.unit ?? "--"
          }`}
        />
      );
    }

    if (data?.valueCodeableConcept) {
      return (
        <DataFields
          title={title}
          data={codeableConceptText(data?.valueCodeableConcept) ?? "--"}
        />
      );
    }

    if (data?.valueString) {
      return <DataFields title={title} data={data?.valueString ?? "--"} />;
    }

    if (typeof data?.valueBoolean === "boolean") {
      return (
        <DataFields title={title} data={data?.valueBoolean ? "Yes" : "No"} />
      );
    }

    if (data?.valueInteger !== undefined) {
      return <DataFields title={title} data={data?.valueInteger ?? "--"} />;
    }

    if (data?.valueRange) {
      return (
        <DataFields
          title={title}
          data={`${data?.valueRange?.low?.value ?? "--"} ${
            data?.valueRange?.low?.unit ?? ""
          } - ${data?.valueRange?.high?.value ?? "--"} ${
            data?.valueRange?.high?.unit ?? ""
          }`}
        />
      );
    }

    if (data?.valueRatio) {
      return (
        <DataFields
          title={title}
          data={`${data?.valueRatio?.numerator?.value ?? "--"} ${
            data?.valueRatio?.numerator?.unit ?? ""
          } / ${data?.valueRatio?.denominator?.value ?? "--"} ${
            data?.valueRatio?.denominator?.unit ?? ""
          }`}
        />
      );
    }

    if (data?.valueSampledData) {
      return (
        <DataFields title={title} data={data?.valueSampledData?.data ?? "--"} />
      );
    }

    if (data?.valueTime) {
      return <DataFields title={title} data={data?.valueTime ?? "--"} />;
    }

    if (data?.valueDateTime) {
      return (
        <DataFields
          title={title}
          data={`${dateAndTimeConverter(
            +new Date(data?.valueDateTime),
            "secondDate"
          )} | ${dateAndTimeConverter(+new Date(data?.valueDateTime), "Time")}`}
        />
      );
    }

    if (data?.valuePeriod) {
      return (
        <DataFields
          title={title}
          data={`${data?.valuePeriod?.start ?? "--"} - ${
            data?.valuePeriod?.end ?? "--"
          }`}
        />
      );
    }

    return null;
  };

  return (
    <div
      className="padding-left-larger padding-top-larger padding-right-larger"
      data-cy={`Observation-card-${codeableConceptText(props.data?.code)}`}
    >
      <TitleFields title="Observation" />

      <div className="padding-left-medium padding-right-medium">
        <DataFields
          title="Title"
          data={codeableConceptText(props.data?.code)}
        />

        <DataFields title="Status" data={props.data?.status} />

        {/* ADDED: subject (mandatory NDHM). Only shown when resolvable/present. */}
        {props.data?.subject?.reference && (
          <DataFields
            title="Subject"
            data={
              getResourceData(props.value, props.data?.subject?.reference)
                ?.name?.[0]?.text ?? props.data?.subject?.reference
            }
          />
        )}

        {!props.data?.component &&
          renderObservationValue(
            props.data,
            codeableConceptText(props.data?.code)
          )}

        {props.data?.component && (
          <>
            <DataFields
              title="Title"
              data={codeableConceptText(props.data?.code)}
            />

            {props.data?.component?.map((data, index) => (
              <div key={`Observation-${index}`}>
                {renderObservationValue(data, codeableConceptText(data?.code))}
              </div>
            ))}
          </>
        )}

        {props.data?.hasMember && (
          <>
            <DataFields
              title="Title"
              data={codeableConceptText(props.data?.code)}
            />

            {props.data?.hasMember?.map((member, index) => {
              const resolved = getResourceData(props.value, member?.reference);
              if (!resolved) return null;
              return (
                <div key={`Observation-hasMember-${index}`}>
                  {renderObservationValue(
                    resolved,
                    codeableConceptText(resolved?.code)
                  )}
                </div>
              );
            })}
          </>
        )}

        {props.data?.category?.[0] && (
          <DataFields
            title="Category"
            data={codeableConceptText(props.data?.category?.[0])}
          />
        )}

        {props.data?.effectiveDateTime && (
          <DataFields
            title="Effective date and time"
            data={`${dateAndTimeConverter(
              +new Date(props.data?.effectiveDateTime),
              "secondDate"
            )}  |  ${dateAndTimeConverter(
              +new Date(props.data?.effectiveDateTime),
              "Time"
            )}`}
          />
        )}

        {props.data?.effectivePeriod && (
          <DataFields
            title="Effective Period"
            data={`${dateAndTimeConverter(
              +new Date(props.data?.effectivePeriod?.start),
              "secondDate"
            )} | ${dateAndTimeConverter(
              +new Date(props.data?.effectivePeriod?.start),
              "Time"
            )} — ${dateAndTimeConverter(
              +new Date(props.data?.effectivePeriod?.end),
              "secondDate"
            )} | ${dateAndTimeConverter(
              +new Date(props.data?.effectivePeriod?.end),
              "Time"
            )}`}
          />
        )}

        {/* ADDED: interpretation (e.g. Normal / High / Low) */}
        {props.data?.interpretation?.[0] && (
          <DataFields
            title="Interpretation"
            data={codeableConceptText(props.data?.interpretation?.[0])}
          />
        )}

        {/* ADDED: reference range */}
        {props.data?.referenceRange?.[0] && (
          <DataFields
            title="Reference Range"
            data={`${props.data?.referenceRange?.[0]?.low?.value ?? "--"} ${
              props.data?.referenceRange?.[0]?.low?.unit ?? ""
            } - ${props.data?.referenceRange?.[0]?.high?.value ?? "--"} ${
              props.data?.referenceRange?.[0]?.high?.unit ?? ""
            }`}
          />
        )}

        {props.data?.note?.length > 0 && (
          <DataFields
            title="Note"
            data={props.data.note.map((n, index) => (
              <div key={`obs-note-${index}`} className="font-size-medium">
                {n.text}
              </div>
            ))}
          />
        )}
      </div>
    </div>
  );
};

const Condition = (props) => {
  const dataAndTime = props.data?.recordedDate
    ? +new Date(props.data?.recordedDate)
    : "";

  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="Condition-card"
    >
      <TitleFields title="Condition" />

      <div className="padding-medium">
        <DataFields
          title="Description"
          data={codeableConceptText(props.data?.code)}
        />

        {/* ADDED: clinicalStatus (mandatory unless entered-in-error) */}
        <DataFields
          title="Clinical Status"
          data={props.data?.clinicalStatus?.coding?.[0]?.code}
        />

        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        {props.data?.severity && (
          <DataFields
            title="Severity"
            data={codeableConceptText(props.data?.severity)}
          />
        )}

        {(props.data?.onsetDateTime ||
          props.data?.onsetString ||
          props.data?.onsetAge ||
          props.data?.onsetPeriod ||
          props.data?.onsetRange) && (
          <DataFields
            title="Onset"
            data={
              props.data?.onsetDateTime
                ? `${dateAndTimeConverter(
                    +new Date(props.data.onsetDateTime),
                    "secondDate"
                  )} | ${dateAndTimeConverter(
                    +new Date(props.data.onsetDateTime),
                    "Time"
                  )}`
                : props.data?.onsetString ??
                  (props.data?.onsetAge?.value
                    ? `${props.data.onsetAge.value} ${
                        props.data.onsetAge.unit ?? ""
                      }`
                    : undefined) ??
                  (props.data?.onsetPeriod?.start
                    ? `${props.data.onsetPeriod.start} — ${
                        props.data.onsetPeriod.end ?? ""
                      }`
                    : undefined) ??
                  (props.data?.onsetRange?.low || props.data?.onsetRange?.high
                    ? `${props.data.onsetRange?.low?.value ?? "--"} ${
                        props.data.onsetRange?.low?.unit ?? ""
                      } - ${props.data.onsetRange?.high?.value ?? "--"} ${
                        props.data.onsetRange?.high?.unit ?? ""
                      }`
                    : undefined)
            }
          />
        )}

        {props.data?.recordedDate && (
          <DataFields
            title="Recorded Date"
            data={`${dateAndTimeConverter(dataAndTime, "secondDate")} ${" | "}
              ${dateAndTimeConverter(dataAndTime, "Time")}`}
          />
        )}

        {props.data?.note?.length > 0 && (
          <DataFields
            title="Note"
            data={props.data.note.map((n, index) => (
              <div key={`cond-note-${index}`} className="font-size-medium">
                {n.text}
              </div>
            ))}
          />
        )}
      </div>
    </div>
  );
};

const Appointment = (props) => {
  const endDataAndTime = +new Date(props.data?.end);
  const StartDataAndTime = +new Date(props.data?.start);

  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="Appointment-card"
    >
      <TitleFields title="Appointment" />

      <div className="padding-medium">
        <DataFields
          title="Title"
          data={codeableConceptText(props.data?.serviceCategory?.[0])}
        />
        <DataFields title="Status" data={props.data?.status} />
        <DataFields
          title="Appointment Type"
          data={codeableConceptText(props.data?.appointmentType)}
        />
        <DataFields
          title="Service Type"
          data={codeableConceptText(props.data?.serviceType?.[0])}
        />
        <DataFields
          title="Specialty"
          data={codeableConceptText(props.data?.specialty?.[0])}
        />
        {props.data?.reasonReference && (
          <DataFields
            title="Reason"
            data={codeableConceptText(
              getResourceData(
                props.value,
                props.data?.reasonReference?.[0]?.reference
              )?.code
            )}
          />
        )}

        {props.data?.reasonCode && (
          <DataFields
            title="Reason"
            data={codeableConceptText(props.data?.reasonCode?.[0])}
          />
        )}

        {/* ADDED: participant (mandatory 1..*) */}
        {props.data?.participant && (
          <DataFields
            title="Participant"
            data={props.data?.participant?.map((p, index) => (
              <div
                className="font-size-large"
                key={`appointment-part-${index}`}
              >
                {getResourceData(props.value, p?.actor?.reference)?.name?.[0]
                  ?.text ??
                  p?.actor?.display ??
                  p?.actor?.reference}{" "}
                ({p?.status})
              </div>
            ))}
          />
        )}

        <DataFields
          title="Timing"
          data={
            <>
              <div className="font-size-medium padding-top-default">
                Start : {dateAndTimeConverter(StartDataAndTime, "secondDate")}{" "}
                {" | "}
                {dateAndTimeConverter(StartDataAndTime, "Time")}
              </div>
              <div className="font-size-medium padding-top-default">
                End : {dateAndTimeConverter(endDataAndTime, "secondDate")}{" "}
                {" | "}
                {dateAndTimeConverter(endDataAndTime, "Time")}
              </div>
            </>
          }
        />
      </div>
    </div>
  );
};

const CarePlan = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="CarePlan-card"
    >
      <TitleFields title="CarePlan" />

      <div className="padding-medium">
        <DataFields title="Title" data={props.data?.title} />
        <DataFields title="Status" data={props.data?.status} />
        <DataFields title="Description" data={props.data?.description} />
        <DataFields title="Intent" data={props.data?.intent} />
        <DataFields
          title="Category"
          data={codeableConceptText(props.data?.category?.[0])}
        />
        {props.data?.goal && (
          <DataFields title="Goal" data={props.data?.goal?.[0]?.display} />
        )}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        {/* activity is 0..* — it carries the plan's actual actions; render it.
            activity.detail.status is mandatory within each activity. */}
        {props.data?.activity?.length > 0 && (
          <DataFields
            title="Activity"
            data={props.data?.activity?.map((activity, index) => (
              <div
                className="font-size-large padding-top-default"
                key={`careplan-activity-${index}`}
              >
                {codeableConceptText(activity?.detail?.code) ??
                  activity?.detail?.description ??
                  getResourceData(props.value, activity?.reference?.reference)
                    ?.code?.text}
                {activity?.detail?.status ? ` (${activity.detail.status})` : ""}
              </div>
            ))}
          />
        )}
      </div>
    </div>
  );
};

const AllergyIntolerance = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`AllergyIntolerance-card-${props.data?.category?.[0] ?? "--"}`}
    >
      <TitleFields title="Allergy" />

      <div className="padding-medium">
        {/* FIX: optional chaining on coding[0] (code is mandatory but may be text-only) */}
        <DataFields
          title="Title"
          data={codeableConceptText(props.data?.code)}
        />

        {/* ADDED: clinicalStatus (mandatory unless refuted/entered-in-error) */}
        <DataFields
          title="Clinical Status"
          data={props.data?.clinicalStatus?.coding?.[0]?.code}
        />

        <DataFields title="Severity" data={props.data?.criticality} />

        {/* FIX: onset[x] is a choice element — was only checking onsetString,
            so an onsetDateTime/onsetAge/onsetPeriod/onsetRange value never
            rendered (mirrors Condition's onset handling) */}
        <DataFields
          title="Duration"
          data={
            props.data?.onsetDateTime
              ? `${dateAndTimeConverter(
                  +new Date(props.data.onsetDateTime),
                  "secondDate"
                )} | ${dateAndTimeConverter(
                  +new Date(props.data.onsetDateTime),
                  "Time"
                )}`
              : props.data?.onsetString ??
                (props.data?.onsetAge?.value
                  ? `${props.data.onsetAge.value} ${
                      props.data.onsetAge.unit ?? ""
                    }`
                  : undefined) ??
                (props.data?.onsetPeriod?.start
                  ? `${props.data.onsetPeriod.start} — ${
                      props.data.onsetPeriod.end ?? ""
                    }`
                  : undefined) ??
                (props.data?.onsetRange?.low || props.data?.onsetRange?.high
                  ? `${props.data.onsetRange?.low?.value ?? "--"} ${
                      props.data.onsetRange?.low?.unit ?? ""
                    } - ${props.data.onsetRange?.high?.value ?? "--"} ${
                      props.data.onsetRange?.high?.unit ?? ""
                    }`
                  : undefined)
          }
        />
        {/* FIX: missing optional chaining after getResourceData() would crash */}
        <DataFields
          title="Patient"
          data={
            getResourceData(props.value, props.data?.patient?.reference)
              ?.name?.[0]?.text ?? props.data?.patient?.reference
          }
        />

        {/* reaction.manifestation is 1..* within a reaction — surface it */}
        {props.data?.reaction?.length > 0 && (
          <DataFields
            title="Reaction"
            data={props.data?.reaction?.map((reaction, rIndex) =>
              reaction?.manifestation?.map((manifestation, mIndex) => (
                <div
                  className="font-size-large"
                  key={`allergy-reaction-${rIndex}-${mIndex}`}
                >
                  {codeableConceptText(manifestation)}
                </div>
              ))
            )}
          />
        )}

        {props.data?.note && (
          <DataFields
            title="Notes"
            data={props.data?.note.map((data, index) => (
              <div
                key={`allergyIntolerance${index}`}
                className="font-size-medium text-capitalize"
              >
                {data.text}
              </div>
            ))}
          />
        )}
      </div>
    </div>
  );
};

const Procedure = (props) => {
  const dataAndTime = +new Date(props.data?.performedDateTime);
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="Procedure-card"
    >
      <TitleFields title="Procedure" />
      <div className="padding-medium">
        {/* FIX: optional chaining on coding[0] (code is mandatory) */}
        <DataFields
          title="Title"
          data={codeableConceptText(props.data?.code)}
        />
        <DataFields title="Status" data={props.data?.status} />
        <DataFields
          title="Status Reason"
          data={codeableConceptText(props.data?.statusReason)}
        />
        <DataFields
          title="Category"
          data={codeableConceptText(props.data?.category)}
        />
        <DataFields
          title="Body Site"
          data={codeableConceptText(props.data?.bodySite?.[0])}
        />
        <DataFields
          title="Outcome"
          data={codeableConceptText(props.data?.outcome)}
        />
        <DataFields
          title="Used Code"
          data={codeableConceptText(props.data?.usedCode?.[0])}
        />
        {/* subject is mandatory */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        {props.data?.performer && (
          <DataFields
            title="Performer"
            data={props.data?.performer?.map((data, index) => {
              const doctor = getResourceData(
                props.value,
                data.actor?.reference
              );
              return (
                <div key={`procedure-perf-${index}`}>
                  <div className="font-size-large">
                    {doctor?.name?.[0]?.text ?? "--"}
                  </div>
                  <div className="font-size-small padding-top-small">
                    {doctor?.name?.[0]?.suffix?.[0] ?? "--"}
                  </div>
                </div>
              );
            })}
          />
        )}

        {/* performed[x] is 0..1 — guard so an absent date isn't "Invalid Date" */}
        {props.data?.performedDateTime && (
          <DataFields
            title="Performed Date"
            data={`${dateAndTimeConverter(dataAndTime, "secondDate")} ${" | "}
          ${dateAndTimeConverter(dataAndTime, "Time")}`}
          />
        )}

        <DataFields
          title="Complications"
          data={props.data?.complication?.map((data, index) => (
            <div
              key={`Procedure-${index}`}
              className="font-size-large padding-top-default"
            >
              {codeableConceptText(data)}
            </div>
          ))}
        />
      </div>
    </div>
  );
};

const DiagnosticReport = (props) => {
  const resource = props.data?.presentedForm?.[0];
  const extension = resource?.contentType?.split("/")?.[1];
  const documentBuffer = resource?.data
    ? base64ToArrayBuffer(resource?.data)
    : null; // FIX: don't decode when there's no data

  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`DiagnosticReport-card-${codeableConceptText(props.data?.code)}`}
    >
      <TitleFields title="Diagnostic Report" />

      <div className="padding-medium">
        {/* code is mandatory */}
        <DataFields
          title="Title"
          data={codeableConceptText(props.data?.code)}
        />
        <DataFields title="Status" data={props.data?.status} />
        {/* subject is 0..1 (Must Support, not mandatory in this profile) */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        {props.data?.category && (
          <DataFields
            title="Category"
            data={codeableConceptText(props.data?.category?.[0])}
          />
        )}

        {/* ADDED: reported / effective date */}
        {(props.data?.issued || props.data?.effectiveDateTime) && (
          <DataFields
            title="Reported On"
            data={`${dateAndTimeConverter(
              +new Date(props.data?.issued ?? props.data?.effectiveDateTime),
              "secondDate"
            )} | ${dateAndTimeConverter(
              +new Date(props.data?.issued ?? props.data?.effectiveDateTime),
              "Time"
            )}`}
          />
        )}

        {props.data?.imagingStudy && (
          <ImagingStudy
            value={props.value}
            data={getResourceData(
              props.value,
              props.data?.imagingStudy?.[0]?.reference
            )}
          />
        )}

        {props.data?.specimen && (
          <Specimen
            value={props.value}
            data={getResourceData(
              props.value,
              props.data?.specimen?.[0]?.reference
            )}
          />
        )}

        {/* media is 1..* (mandatory in the Imaging profile); the Media
            reference lives on media.link, not media directly */}
        {props.data?.media?.map((media, index) => (
          <Media
            key={`diag-media-${index}`}
            value={props.value}
            data={getResourceData(
              props.value,
              media?.link?.reference ?? media?.reference
            )}
          />
        ))}

        {/* resultsInterpreter is 1..* — render every interpreter */}
        <DataFields
          title="Results Interpreter"
          data={props.data?.resultsInterpreter?.map((interpreter, index) => (
            <div className="font-size-large" key={`diag-interpreter-${index}`}>
              {getResourceData(props.value, interpreter?.reference)?.name?.[0]
                ?.text ?? interpreter?.display}
            </div>
          ))}
        />
        <DataFields
          title="Specimen"
          data={codeableConceptText(
            getResourceData(props.value, props.data?.specimen?.[0]?.reference)
              ?.type
          )}
        />
        <DataFields title="Conclusion" data={props.data?.conclusion} />

        {props.data?.basedOn && (
          <DataFields
            title="Based On"
            data={getComponents({
              entryData: props.value,
              type: getResourceData(
                props.value,
                props.data?.basedOn?.[0]?.reference
              )?.resourceType,
              index: 1,
              data: { reference: props.data?.basedOn?.[0]?.reference },
              documentOnClick: props.documentOnClick
            })}
          />
        )}

        {props.data?.result?.map((data, index) => (
          <div key={`diag-result-${index}`}>
            <Observation
              value={props.value}
              data={getResourceData(props.value, data?.reference)}
            />
          </div>
        ))}

        {resource?.data && (
          <DataFields
            title="Data"
            data={
              allowSupportedFileFormat(resource?.contentType) ? (
                <div
                  className=" flex-justify-content-space-between flex-direction-column"
                  onClick={() => {
                    if (documentBuffer) props.documentOnClick(documentBuffer);
                  }}
                >
                  <div className="padding-top-small">
                    <div className="font-size-large">
                      {resource?.title ?? "--"}.{extension ?? "--"}
                    </div>
                  </div>
                  <div
                    data-cy="pdfRenderer"
                    className="padding-top-large padding-bottom-default width-40-percentage"
                  >
                    <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
                      <PDFIcon height="48" width="48" />
                      <div className="adaptive-padding-top">View file</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="padding-top-large padding-bottom-default width-40-percentage">
                  <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
                    <img
                      data-cy="unSupported-icon"
                      draggable="false"
                      className="height-width-48px"
                      src={unsupportedIcon}
                      alt="unsupported icon"
                    />
                    <div className="adaptive-padding-top">UnSupported</div>
                  </div>
                </div>
              )
            }
          />
        )}
      </div>
    </div>
  );
};

const Binary = (props) => {
  const extension = props.data?.contentType?.split("/")[1];
  const documentBuffer = props.data?.data
    ? base64ToArrayBuffer(props.data?.data)
    : null;

  return (
    <div
      className="padding-left-larger padding-top-larger padding-right-larger"
      data-cy="Binary-card"
    >
      <TitleFields title="Binary" />
      <div className="padding-medium">
        {/* contentType is mandatory */}
        <DataFields title="Content Type" data={props.data?.contentType} />
        {allowSupportedFileFormat(props.data?.contentType) ? (
          <div
            className=" flex-justify-content-space-between flex-direction-column"
            onClick={() => {
              if (documentBuffer) props.documentOnClick(documentBuffer);
            }}
          >
            <div className="padding-top-small">
              <div className="font-size-large">
                {props.data?.id ?? "--"}.{extension ?? "--"}
              </div>
            </div>
            <div
              data-cy="pdfRenderer"
              className="padding-top-large padding-bottom-default width-40-percentage"
            >
              <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
                <PDFIcon height="48" width="48" />
                <div className="adaptive-padding-top">View file</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="padding-top-large padding-bottom-default width-40-percentage">
            <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
              <img
                data-cy="unSupported-icon"
                draggable="false"
                className="height-width-48px"
                src={unsupportedIcon}
                alt="unsupported icon"
              />
              <div className="adaptive-padding-top">UnSupported</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ChargeItem = (props) => {
  // product[x] is mandatory (1..1) in NDHM — handle BOTH choice variants:
  // productReference (resolved) and productCodeableConcept
  const product = getResourceData(
    props.value,
    props.data?.productReference?.reference
  );
  const productDisplay =
    codeableConceptText(props.data?.productCodeableConcept) ??
    codeableConceptText(product?.code) ??
    props.data?.productReference?.display;
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`ChargeItem-card-${codeableConceptText(props.data?.code)}`}
    >
      <TitleFields title="Charge Item" />
      <div className="padding-medium">
        {/* code is mandatory */}
        <DataFields
          title="Title"
          data={codeableConceptText(props.data?.code)}
        />
        <DataFields title="Status" data={props.data?.status} />
        {productDisplay && <DataFields title="Product" data={productDisplay} />}
        {props.data?.quantity && (
          <DataFields
            title="Quantity"
            data={`${props.data?.quantity?.value ?? "--"} ${
              props.data?.quantity?.unit ?? ""
            }`}
          />
        )}

        {/* FIX: occurrence[x] flattens to a top-level occurrenceDateTime */}
        {props.data?.occurrenceDateTime && (
          <DataFields
            title="Occurrence"
            data={`${dateAndTimeConverter(
              +new Date(props.data?.occurrenceDateTime),
              "secondDate"
            )} | ${dateAndTimeConverter(
              +new Date(props.data?.occurrenceDateTime),
              "Time"
            )}`}
          />
        )}

        {/* subject is mandatory */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
      </div>
    </div>
  );
};

const FamilyMemberHistory = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`FamilyMemberHistory-card`}
    >
      <TitleFields title="Family Member History" />
      <div className="padding-medium">
        <DataFields title="Status" data={props.data?.status} />
        <DataFields
          title="Patient"
          data={
            getResourceData(props.value, props.data?.patient?.reference)
              ?.name?.[0]?.text ?? props.data?.patient?.reference
          }
        />
        <DataFields
          title="Relationship"
          data={codeableConceptText(props.data?.relationship)}
        />
        <DataFields
          title="Reason"
          data={codeableConceptText(props.data?.reasonCode?.[0])}
        />

        {/* condition is 1..* — render every condition */}
        <DataFields
          title="Condition"
          data={props.data?.condition?.map((condition, index) => (
            <div className="font-size-large" key={`fmh-condition-${index}`}>
              {codeableConceptText(condition?.code)}
            </div>
          ))}
        />
      </div>
    </div>
  );
};

const ImagingStudy = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      // FIX: ImagingStudy has no `code` element — was crashing on code.coding[0]
      data-cy={`ImagingStudy-card-${
        props.data?.series?.[0]?.modality?.display ?? "study"
      }`}
    >
      <TitleFields title="Imaging Study" />
      <div className="padding-medium">
        {/* FIX: use description/modality instead of non-existent code */}
        <DataFields
          title="Title"
          data={
            props.data?.description ??
            codeableConceptText(props.data?.procedureCode?.[0])
          }
        />
        <DataFields title="Status" data={props.data?.status} />
        <DataFields
          title="Number Of Series"
          data={props.data?.numberOfSeries}
        />
        <DataFields
          title="Number Of Instances"
          data={props.data?.numberOfInstances}
        />
        <DataFields
          title="Procedure"
          data={codeableConceptText(props.data?.procedureCode?.[0])}
        />
        <DataFields
          title="Reason"
          data={codeableConceptText(props.data?.reasonCode?.[0])}
        />
        <DataFields title="UID" data={props.data?.series?.[0]?.uid} />
        <DataFields
          title="Modality"
          data={props.data?.series?.[0]?.modality?.display}
        />
        {/* instance.uid and instance.sopClass are mandatory (1..1) */}
        <DataFields
          title="Instance"
          data={
            props.data?.series?.[0]?.instance?.[0]?.title ??
            props.data?.series?.[0]?.instance?.[0]?.uid
          }
        />
        <DataFields
          title="SOP Class"
          data={
            props.data?.series?.[0]?.instance?.[0]?.sopClass?.display ??
            props.data?.series?.[0]?.instance?.[0]?.sopClass?.code
          }
        />
        {/* subject is mandatory */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
      </div>
    </div>
  );
};

const ImmunizationRecommendation = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      // FIX: ImmunizationRecommendation has no `code` element — was crashing
      data-cy={`ImmunizationRecommendation-card-${
        props.data?.recommendation?.[0]?.vaccineCode?.[0]?.coding?.[0]
          ?.display ?? "recommendation"
      }`}
    >
      <TitleFields title="Immunization Recommendation" />
      <div className="padding-medium">
        {/* ADDED: patient (mandatory) */}
        <DataFields
          title="Patient"
          data={
            getResourceData(props.value, props.data?.patient?.reference)
              ?.name?.[0]?.text ?? props.data?.patient?.reference
          }
        />
        {/* ADDED: date (mandatory) */}
        <DataFields
          title="Date"
          data={
            props.data?.date
              ? `${dateAndTimeConverter(
                  +new Date(props.data?.date),
                  "secondDate"
                )} | ${dateAndTimeConverter(
                  +new Date(props.data?.date),
                  "Time"
                )}`
              : undefined
          }
        />
        {/* recommendation is 1..* — render every recommendation */}
        {props.data?.recommendation?.map((recommendation, index) => (
          <div key={`immrec-${index}`}>
            <DataFields
              title="Vaccine Code"
              data={codeableConceptText(recommendation?.vaccineCode?.[0])}
            />
            <DataFields
              title="Target Disease"
              data={codeableConceptText(recommendation?.targetDisease)}
            />
            <DataFields
              title="Contraindicated VaccineCode"
              data={codeableConceptText(
                recommendation?.contraindicatedVaccineCode?.[0]
              )}
            />
            {/* recommendation.forecastStatus is mandatory */}
            <DataFields
              title="Forecast Status"
              data={codeableConceptText(recommendation?.forecastStatus)}
            />
            <DataFields
              title="Date Criterion"
              data={codeableConceptText(
                recommendation?.dateCriterion?.[0]?.code
              )}
            />
            {/* dateCriterion.value (the date) is mandatory when present */}
            <DataFields
              title="Date Criterion Value"
              data={recommendation?.dateCriterion?.[0]?.value}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const Invoice = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="Invoice-card"
    >
      <TitleFields title="Invoice" />

      <div className="padding-medium">
        {/* status is mandatory */}
        <DataFields title="Status" data={props.data?.status} />

        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        <DataFields
          title="Identifier"
          data={props.data?.identifier?.[0]?.value}
        />
        <DataFields title="Type" data={codeableConceptText(props.data?.type)} />
        {props.data?.date && (
          <DataFields
            title="Date"
            data={`${dateAndTimeConverter(
              +new Date(props.data?.date),
              "secondDate"
            )} | ${dateAndTimeConverter(+new Date(props.data?.date), "Time")}`}
          />
        )}
        {props.data?.lineItem?.map((data, lineIndex) => (
          <div key={`invoice-line-${lineIndex}`}>
            <DataFields
              title="Charge Item"
              data={
                codeableConceptText(
                  getResourceData(
                    props.value,
                    data?.chargeItemReference?.reference
                  )?.code
                ) ?? data?.chargeItemReference?.reference
              }
            />
            {data?.priceComponent?.map((element, priceIndex) => (
              <div key={`invoice-price-${lineIndex}-${priceIndex}`}>
                <div>{element?.type}</div>
                <DataFields
                  title={codeableConceptText(element?.code)}
                  data={`${element?.amount?.value ?? "--"} ${
                    element?.amount?.currency ?? ""
                  }`}
                />
              </div>
            ))}
          </div>
        ))}

        {props.data?.totalGross && (
          <DataFields
            title="Total Gross"
            data={`${props.data?.totalGross?.value ?? "--"} ${
              props.data?.totalGross?.currency ?? ""
            }`}
          />
        )}
        {props.data?.totalNet && (
          <DataFields
            title="Total Net"
            data={`${props.data?.totalNet?.value ?? "--"} ${
              props.data?.totalNet?.currency ?? ""
            }`}
          />
        )}
      </div>
    </div>
  );
};

const Media = (props) => {
  const resource = props.data?.content;
  const extension = resource?.contentType?.split("/")[1];
  const documentBuffer = resource?.data
    ? base64ToArrayBuffer(resource?.data)
    : null;

  return (
    <div
      className="padding-left-larger padding-right-larger"
      // FIX: Media has no `code` element — was crashing on code.coding[0]
      data-cy={`Media-card-${
        codeableConceptText(props.data?.type) ?? props.data?.id ?? "media"
      }`}
    >
      <TitleFields title="Media" />

      <div className="padding-medium">
        {/* status & content are mandatory */}
        <DataFields title="Status" data={props.data?.status} />

        <DataFields
          title="Modality"
          data={codeableConceptText(props.data?.modality)}
        />

        {allowSupportedFileFormat(resource?.contentType) ? (
          <div
            className=" flex-justify-content-space-between flex-direction-column"
            onClick={() => {
              if (documentBuffer) props.documentOnClick(documentBuffer);
            }}
          >
            <div className="padding-top-small">
              <div className="font-size-large">
                {resource?.title ?? props.data?.id ?? "--"}.{extension ?? "--"}
              </div>
            </div>
            <div
              data-cy="pdfRenderer"
              className="padding-top-large padding-bottom-default width-40-percentage"
            >
              <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
                <PDFIcon height="48" width="48" />
                <div className="adaptive-padding-top">View file</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="padding-top-large padding-bottom-default width-40-percentage">
            <div className="inherit-parent-width display-flex flex-direction-column border-radius-default flex-center-children-horizontally flex-center-children-vertically height-120px background-color-grey">
              <img
                data-cy="unSupported-icon"
                draggable="false"
                className="height-width-48px"
                src={unsupportedIcon}
                alt="unsupported icon"
              />
              <div className="adaptive-padding-top">UnSupported</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MedicationStatement = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`MedicationStatement-card`}
    >
      <TitleFields title="Medication Statement" />
      <div className="padding-medium">
        <DataFields title="Status" data={props.data?.status} />
        {/* medication[x] is mandatory */}
        <DataFields
          title="Medication"
          data={
            codeableConceptText(props.data?.medicationCodeableConcept) ??
            codeableConceptText(
              getResourceData(
                props.value,
                props.data?.medicationReference?.reference
              )?.code
            )
          }
        />
        {/* FIX: MedicationStatement's dosage element is `dosage`, not `dosageInstruction` */}
        {props.data?.dosage && (
          <DataFields
            title="Dosage Instruction"
            data={props.data?.dosage?.map((data, index) => (
              <div
                className="padding-left-larger padding-right-larger"
                key={`medicationStatement-${index}`}
              >
                <div className="font-size-large text-capitalize padding-top-default">
                  {data.text}
                </div>
                <DataFields
                  title="Additional Instruction"
                  data={codeableConceptText(data?.additionalInstruction?.[0])}
                />
                <DataFields
                  title="Method"
                  data={codeableConceptText(data?.method)}
                />
                <DataFields
                  title="Route"
                  data={codeableConceptText(data?.route)}
                />
              </div>
            ))}
          />
        )}
        {/* reasonCode is 0..* — render every reason */}
        {props.data?.reasonCode?.length > 0 && (
          <DataFields
            title="Reason"
            data={props.data?.reasonCode?.map((reason, index) => (
              <div className="font-size-large" key={`medstmt-reason-${index}`}>
                {codeableConceptText(reason)}
              </div>
            ))}
          />
        )}
        {/* subject is mandatory */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
      </div>
    </div>
  );
};

const PractitionerRole = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`PractitionerRole-card`}
    >
      <TitleFields title="Practitioner Role" />
      <div className="padding-medium">
        {/* code is 0..* — render every role code */}
        {props.data?.code?.length > 0 && (
          <DataFields
            title="Title"
            data={props.data?.code?.map((code, index) => (
              <div
                className="font-size-large"
                key={`practitionerRole-code-${index}`}
              >
                {codeableConceptText(code)}
              </div>
            ))}
          />
        )}
        <DataFields
          title="Practitioner"
          data={
            getResourceData(props.value, props.data?.practitioner?.reference)
              ?.name?.[0]?.text ?? props.data?.practitioner?.display
          }
        />

        <DataFields
          title="Assigner"
          data={props.data?.identifier?.[0]?.assigner?.display}
        />

        <DataFields
          title="Organization"
          data={
            getResourceData(props.value, props.data?.organization?.reference)
              ?.name ?? props.data?.organization?.display
          }
        />
        {/* specialty is 0..* — render every specialty */}
        {props.data?.specialty?.length > 0 && (
          <DataFields
            title="Speciality"
            data={props.data?.specialty?.map((specialty, index) => (
              <div
                className="font-size-large"
                key={`practitionerRole-spec-${index}`}
              >
                {codeableConceptText(specialty)}
              </div>
            ))}
          />
        )}
        <DataFields
          title={
            codeableConceptText(props.data?.identifier?.[0]?.type) ??
            "Identifier"
          }
          data={props.data?.identifier?.[0]?.value}
        />
        {props.data?.telecom?.map((data, index) => (
          <div key={`practitionerRole-${index}`}>
            <DataFields title={data?.system ?? "Contact"} data={data?.value} />
          </div>
        ))}
      </div>
    </div>
  );
};

const ServiceRequest = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy={`ServiceRequest-card-${codeableConceptText(props.data?.code)}`}
    >
      <TitleFields title="Service Request" />
      <div className="padding-medium">
        <DataFields
          title="Title"
          data={codeableConceptText(props.data?.code)}
        />
        <DataFields title="Status" data={props.data?.status} />
        <DataFields title="Intent" data={props.data?.intent} />
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />

        <DataFields
          title="Requester"
          data={getComponents({
            entryData: props.value,
            type: getResourceData(props.value, props.data?.requester?.reference)
              ?.resourceType,
            index: 1,
            data: { reference: props.data?.requester?.reference },
            documentOnClick: props.documentOnClick
          })}
        />
      </div>
    </div>
  );
};

const Specimen = (props) => {
  return (
    <div
      className="padding-left-larger padding-right-larger"
      data-cy="Specimen-card"
    >
      <TitleFields title="Specimen" />

      <div className="padding-medium">
        <DataFields title="Type" data={codeableConceptText(props.data?.type)} />
        {/* subject is 0..1 (Must Support, not mandatory in this profile) */}
        <DataFields
          title="Subject"
          data={
            getResourceData(props.value, props.data?.subject?.reference)
              ?.name?.[0]?.text ?? props.data?.subject?.reference
          }
        />
        {/* collection.collected[x] is mandatory (1..1): dateTime OR Period */}
        {props.data?.collection?.collectedDateTime && (
          <DataFields
            title="Collected Time"
            data={dateAndTimeConverter(
              props.data?.collection?.collectedDateTime,
              "secondDate"
            )}
          />
        )}
        {props.data?.collection?.collectedPeriod && (
          <DataFields
            title="Collected Period"
            data={`${dateAndTimeConverter(
              props.data?.collection?.collectedPeriod?.start,
              "secondDate"
            )} — ${dateAndTimeConverter(
              props.data?.collection?.collectedPeriod?.end,
              "secondDate"
            )}`}
          />
        )}
        {props.data?.receivedTime && (
          <DataFields
            title="Received Time"
            data={dateAndTimeConverter(props.data?.receivedTime, "secondDate")}
          />
        )}
      </div>
    </div>
  );
};

const TitleFields = (props) => {
  return (
    <div className="font-size-medium text-uppercase font-family-gilroy-medium">
      {props.title}
    </div>
  );
};

const DataFields = (props) => {
  if (props.data == null) return null;
  return (
    <>
      {props.title !== "EMPTY" && (
        <div className="font-size-large padding-top-large font-family-gilroy-medium text-capitalize">
          {props.title ?? "--"}
        </div>
      )}
      {props.data !== "EMPTY" && (
        <div className="font-size-medium padding-top-small text-capitalize">
          {props.data}
        </div>
      )}
    </>
  );
};

const DropDownButton = (props) => {
  return (
    <div
      className={`${props.className} cursor-pointer`}
      onClick={props.dropDownClick}
      data-cy={props["dropDownButton-data-cy"]}
    >
      <img
        className={`${
          props.showData === true ? "rotate-180" : ""
        } padding-small`}
        src={dropDownArrow}
        alt="dropdown"
      />
    </div>
  );
};

const getResourceData = (value, fullUrl) => {
  if (!value || !fullUrl) return undefined; // FIX: guard against undefined args
  const data = value?.entry?.find((data) => data?.fullUrl === fullUrl);
  return data?.resource;
};

const allowSupportedFileFormat = (data) => {
  return data === "application/pdf";
};

const getComponents = (props) => {
  switch (props.type) {
    case "Patient": {
      return (
        <Patient
          key={props.index}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }

    case "Practitioner": {
      return (
        <Practitioner
          key={props.index}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Encounter": {
      return (
        <Encounter
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Composition": {
      return (
        <DateAndTime
          key={props.index}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "DocumentReference": {
      return (
        <DocumentReference
          key={props.index}
          data={getResourceData(props.entryData, props.data?.reference)}
          documentOnClick={props.documentOnClick}
        />
      );
    }

    case "Media": {
      return (
        <Media
          key={props.index}
          data={getResourceData(props.entryData, props.data?.reference)}
          documentOnClick={props.documentOnClick}
        />
      );
    }
    case "MedicationStatement": {
      return (
        <MedicationStatement
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "PractitionerRole": {
      return (
        <PractitionerRole
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Immunization": {
      return (
        <Immunization
          key={props.index}
          value={props.entryData}
          documentOnClick={props.documentOnClick}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "ImmunizationRecommendation": {
      return (
        <ImmunizationRecommendation
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "ChargeItem": {
      return (
        <ChargeItem
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "FamilyMemberHistory": {
      return (
        <FamilyMemberHistory
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "ImagingStudy": {
      return (
        <ImagingStudy
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Invoice": {
      return (
        <Invoice
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "ServiceRequest": {
      return (
        <ServiceRequest
          key={props.index}
          value={props.entryData}
          documentOnClick={props.documentOnClick}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Specimen": {
      return (
        <Specimen
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Binary": {
      return (
        <Binary
          key={props.index}
          value={props.entryData}
          documentOnClick={props.documentOnClick}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Appointment": {
      return (
        <Appointment
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "CarePlan": {
      return (
        <CarePlan
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Condition": {
      return (
        <Condition
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "AllergyIntolerance": {
      return (
        <AllergyIntolerance
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Procedure": {
      return (
        <Procedure
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Observation": {
      return (
        <Observation
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "DiagnosticReport": {
      return (
        <DiagnosticReport
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
          documentOnClick={props.documentOnClick}
        />
      );
    }
    case "Organization": {
      return (
        <Organization
          key={props.index}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    case "Medication": {
      return (
        <Medication
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }

    case "MedicationRequest": {
      return (
        <MedicationRequest
          key={props.index}
          value={props.entryData}
          data={getResourceData(props.entryData, props.data?.reference)}
        />
      );
    }
    default: {
      return <div key={props.index} />;
    }
  }
};

function isValidBase64(str) {
  if (typeof str !== "string" || str.length === 0) return false; // FIX: guard non-strings
  try {
    return btoa(atob(str)) === str;
  } catch {
    return false;
  }
}

function base64ToArrayBuffer(base64) {
  if (!isValidBase64(base64)) {
    console.error("Invalid base64 string:", base64);
    return null;
  }

  try {
    const binary_string = window?.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (error) {
    console.error("Error decoding base64 string:", error);
    return null;
  }
}

FHIRRenderer.propTypes = {
  data: PropTypes.object,
  documentOnClick: PropTypes.func,
  userType: PropTypes.oneOf(["clinic", "patient", "doctor"]),
  document: PropTypes.object
};

FHIRRenderer.defaultProps = {
  data: null,
  documentOnClick: undefined,
  userType: "",
  document: undefined
};
